<template>
  <v-card>
    <v-menu v-model="startDateMenu" :close-on-content-click="false">
      <template v-slot:activator="{ props }">
        <v-btn
          v-bind="props"
          prepend-icon="mdi-calendar"
          variant="text"
          class="px-2"
          :style="{ textTransform: 'capitalize' }"
          data-testid="date-picker-start"
          aria-label="Start date picker"
        >
          {{ formatDate(startDate) }}
        </v-btn>
      </template>
      <v-date-picker
        title="Start Date"
        :model-value="startDate"
        :max="maxDate"
        @update:model-value="handleStartDateChange"
      />
    </v-menu>
    <span>-</span>
    <v-menu v-model="endDateMenu" :close-on-content-click="false">
      <template v-slot:activator="{ props }">
        <v-btn
          v-bind="props"
          prepend-icon="mdi-calendar"
          variant="text"
          class="pl-3 pr-2"
          :style="{ textTransform: 'capitalize' }"
          data-testid="date-picker-end"
          aria-label="End date picker"
        >
          {{ formatDate(endDate) }}
        </v-btn>
      </template>
      <v-date-picker
        title="End Date"
        :model-value="endDate"
        :max="maxDate"
        :min="endMinDate"
        @update:model-value="handleEndDateChange"
      />
    </v-menu>
  </v-card>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

const props = defineProps<{
  startDate: Date | null
  endDate: Date | null
  maxDate?: Date | null
}>()

const emit = defineEmits<{
  'update:startDate': [date: Date]
  'update:endDate': [date: Date]
}>()

const currentDate = new Date()
const maxDate = computed(() => props.maxDate ?? currentDate)
const startDateMenu = ref(false)
const endDateMenu = ref(false)

const endMinDate = computed(() => {
  const nextDay = props?.startDate ? new Date(props.startDate) : new Date()
  nextDay.setDate(nextDay.getDate())
  return nextDay
})

const handleStartDateChange = (date: Date | null) => {
  if (!date) {
    return
  }

  startDateMenu.value = false
  emit('update:startDate', date)
}

const handleEndDateChange = (date: Date | null) => {
  if (!date) {
    return
  }

  endDateMenu.value = false
  emit('update:endDate', date)
}

watch(
  () => props.maxDate,
  newMaxDate => {
    if (newMaxDate && props?.endDate && props?.endDate > newMaxDate) {
      emit('update:endDate', newMaxDate)
    }
  },
)

function formatDate(date: Date | null) {
  if (!date) {
    return null
  }

  date = new Date(date)

  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ]

  const year = date.getFullYear()
  const month = months[date.getMonth()]
  const day = String(date.getDate()).padStart(2, '0')

  const currentYear = new Date().getFullYear()
  // Only show year if it's different from current year
  const yearDisplay = year !== currentYear ? ` ${year}` : ''

  return `${month} ${day}${yearDisplay}`
}
</script>
